import React, { useState } from "react";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import InputField from "../../utils/InputField";
import { changePassword } from "../../services/authService";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";

const EMPTY_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" };

function ChangePassword() {
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();

  const [form, setForm] = useState(EMPTY_FORM);
  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.currentPassword) {
      errors.currentPassword = "Current password is required";
    }
    if (!form.newPassword || form.newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters";
    }
    if (form.newPassword !== form.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    if (form.currentPassword && form.currentPassword === form.newPassword) {
      errors.newPassword = "New password must be different from your current password";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setSuccessMessage("");

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});

    try {
      startLoading();
      const data = await changePassword(
        form.currentPassword,
        form.newPassword,
        form.confirmPassword
      );
      const message = data?.message || "Password changed successfully.";
      setSuccessMessage(message);
      showToast(message, "success");
      setForm(EMPTY_FORM);
    } catch (error) {
      // The API returns either { message } or { success:false, errors:[{field,message}] }
      if (Array.isArray(error?.errors)) {
        const fieldErrors = {};
        error.errors.forEach((item) => {
          fieldErrors[item.field] = item.message;
        });
        setValidationErrors(fieldErrors);
        showToast("Please fix the highlighted fields.", "error");
      } else {
        const message = error?.message || "Failed to change password.";
        setServerError(message);
        showToast(message, "error");
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <section className="p-4">
      <Card className="mx-auto" style={{ maxWidth: "560px" }}>
        <Card.Body>
          <h5 className="mb-1 fw-semibold">Change Password</h5>
          <p className="text-muted small mb-4">
            Enter your current password, then choose a new one of at least 8 characters.
          </p>

          {serverError && <Alert variant="danger">{serverError}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="px-0 mb-3">
              <InputField
                field="currentPassword"
                label="Current Password"
                type="password"
                value={form.currentPassword}
                onChange={handleFieldChange}
                error={validationErrors.currentPassword}
                as={Col}
                controlId="account"
              />
            </Row>
            <Row className="px-0 mb-3">
              <InputField
                field="newPassword"
                label="New Password"
                type="password"
                value={form.newPassword}
                onChange={handleFieldChange}
                error={validationErrors.newPassword}
                as={Col}
                controlId="account"
              />
            </Row>
            <Row className="px-0 mb-3">
              <InputField
                field="confirmPassword"
                label="Confirm New Password"
                type="password"
                value={form.confirmPassword}
                onChange={handleFieldChange}
                error={validationErrors.confirmPassword}
                as={Col}
                controlId="account"
              />
            </Row>
            <Button type="submit" className="mt-2">
              Update Password
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </section>
  );
}

export default ChangePassword;
